/**
 * Collapse overlapping keyword keys for match/extract.
 * Mirrors scraper/core/keywords/subsume.py
 */

const WEAK_FILLER_TOKENS = new Set([
  "a",
  "an",
  "and",
  "company",
  "etc",
  "financial",
  "general",
  "global",
  "international",
  "mathematical",
  "of",
  "or",
  "prior",
  "quantitative",
  "related",
  "the",
]);

export function normalizeDictionaryKey(value) {
  let text = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/&/g, " and ");
  text = text.replace(/['\u2019]s\b/g, "s");
  text = text.replace(/[^\w\s/+]/g, " ");
  text = text.replace(/\b(master|bachelor|doctor) s\b/g, "$1s");
  text = text.replace(/\s+/g, " ").trim();
  return text;
}

function tokenWords(value) {
  const key = normalizeDictionaryKey(value);
  return key ? key.split(" ") : [];
}

function contiguousSubseqSpan(shorter, longer) {
  if (!shorter.length || !longer.length || shorter.length >= longer.length) return -1;
  const n = shorter.length;
  for (let i = 0; i <= longer.length - n; i += 1) {
    let ok = true;
    for (let j = 0; j < n; j += 1) {
      if (longer[i + j] !== shorter[j]) {
        ok = false;
        break;
      }
    }
    if (ok) return i;
  }
  return -1;
}

export function isWeakExtension(shorterKey, longerKey) {
  const shorter = tokenWords(shorterKey);
  const longer = tokenWords(longerKey);
  if (shorter.length < 2) return false;
  if (shorter[shorter.length - 1] === "major" && longer[longer.length - 1] === "major") {
    return false;
  }
  const start = contiguousSubseqSpan(shorter, longer);
  if (start < 0) return false;
  const fillers = [...longer.slice(0, start), ...longer.slice(start + shorter.length)];
  return fillers.length > 0 && fillers.every((token) => WEAK_FILLER_TOKENS.has(token));
}

export function isSpecificExtension(shorterKey, longerKey) {
  const shorter = tokenWords(shorterKey);
  const longer = tokenWords(longerKey);
  if (shorter.length < 1) return false;
  const start = contiguousSubseqSpan(shorter, longer);
  if (start < 0) return false;
  if (isWeakExtension(shorterKey, longerKey)) return false;
  return true;
}

function escapeRegExp(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function phraseSpans(haystack, phraseKey) {
  const words = tokenWords(phraseKey);
  if (!words.length) return [];
  const body = words.map(escapeRegExp).join("\\s+");
  const re = new RegExp(`(?<!\\w)${body}(?!\\w)`, "g");
  const spans = [];
  let m;
  while ((m = re.exec(haystack)) !== null) {
    spans.push([m.index, m.index + m[0].length]);
    if (m[0].length === 0) re.lastIndex += 1;
  }
  return spans;
}

/**
 * True when `shorterKey` appears as its own whole phrase, not only as a prefix
 * of `longerKey` ("React, React Native" vs "experience with React Native").
 */
export function shorterHasIndependentMention(sourceText, shorterKey, longerKey) {
  const hay = normalizeDictionaryKey(sourceText);
  if (!hay) return false;
  const shorterSpans = phraseSpans(hay, shorterKey);
  if (!shorterSpans.length) return false;
  const longerSpans = phraseSpans(hay, longerKey);
  return shorterSpans.some(([s0, s1]) => !longerSpans.some(([l0, l1]) => l0 <= s0 && s1 <= l1));
}

/**
 * @param {string[]} keys
 * @param {{ sourceText?: string, collapseSpecific?: boolean }} [opts]
 */
export function collapseOverlappingNormalizedKeys(keys, opts = {}) {
  const sourceText = String(opts.sourceText || "");
  const collapseSpecific = opts.collapseSpecific !== false;
  const unique = [];
  const seen = new Set();
  for (const raw of keys || []) {
    const key = normalizeDictionaryKey(raw);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    unique.push(key);
  }

  const drop = new Set();
  const keepShorter = (shorter, longer) =>
    Boolean(sourceText) && shorterHasIndependentMention(sourceText, shorter, longer);

  for (let i = 0; i < unique.length; i += 1) {
    for (let j = i + 1; j < unique.length; j += 1) {
      const a = unique[i];
      const b = unique[j];
      if (isWeakExtension(a, b)) drop.add(b);
      else if (isWeakExtension(b, a)) drop.add(a);
      else if (collapseSpecific && isSpecificExtension(a, b)) {
        if (!keepShorter(a, b)) drop.add(a);
      } else if (collapseSpecific && isSpecificExtension(b, a)) {
        if (!keepShorter(b, a)) drop.add(b);
      }
    }
  }

  return new Set(unique.filter((key) => !drop.has(key)));
}

/**
 * @param {Array} items
 * @param {(item: any) => string} keyFn
 * @param {{ sourceText?: string, collapseSpecific?: boolean }} [opts]
 */
export function filterItemsByNormalized(items, keyFn, opts = {}) {
  const keys = (items || []).map((item) => keyFn(item));
  const keep = collapseOverlappingNormalizedKeys(keys, opts);
  const result = [];
  const seen = new Set();
  for (let i = 0; i < (items || []).length; i += 1) {
    const key = normalizeDictionaryKey(keys[i]);
    if (!keep.has(key) || seen.has(key)) continue;
    seen.add(key);
    result.push(items[i]);
  }
  return result;
}

export function weakMatchKeys(resumeKey, jobKey) {
  const a = normalizeDictionaryKey(resumeKey);
  const b = normalizeDictionaryKey(jobKey);
  if (!a || !b) return false;
  if (a === b) return true;
  return isWeakExtension(a, b) || isWeakExtension(b, a);
}
